import { SpeakerLabel, SpeakerLabelElementType, SpeakerLabelTextInterface } from '../../../types/Element/VideoElement';
import { getFontObject } from './utils';
import { SelectionElementType } from '@/App/remotion/components/Outlines/types';
import { SpeakerWithDetails } from '@/context/TranscriptContext/TranscriptContextTypes';
import { Clip } from '@/domains/asset';
import { getSpeakerNameAndTitle } from '@/Pages/Clip/CompositePlayer/SpeakerVideoUtils';
import { TEXT_COLOR_DEFAULT } from '@/stores/constants';

const SPEAKER_LABEL_CONFIG = {
  LANDSCAPE: {
    userName: {
      fontSize: '48px'
    },
    userTitle: {
      fontSize: '42px'
    }
  },
  SQUARE: {
    userName: {
      fontSize: '40px'
    },
    userTitle: {
      fontSize: '36px'
    }
  },
  PORTRAIT: {
    userName: {
      fontSize: '50px'
    },
    userTitle: {
      fontSize: '44px'
    }
  }
};

export const getSpeakerLabelConfigForAudiograms = (speaker: SpeakerWithDetails, clip: Clip): SpeakerLabel => {
  const { name, title } = getSpeakerNameAndTitle(speaker);

  const fontObject = getFontObject({ clip, fontTarget: SelectionElementType.SPEAKER_LABEL });

  const textColor =
    clip.asset_metadata.speaker_label_styles?.text_color ||
    clip.asset_metadata.magicLayout?.textColor ||
    TEXT_COLOR_DEFAULT;

  const commonContainerStyles: React.CSSProperties = {
    position: 'absolute',
    background: 'transparent',
    color: textColor,
    alignItems: 'flex-start',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    lineHeight: 'none',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: fontObject?.font?.name
  };

  const userName: SpeakerLabelTextInterface = {
    type: SpeakerLabelElementType.TEXT,
    content: name,
    style: {
      fontFamily: 'inherit',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      // using units such as cqi was leading to difference between preview and final output that was being generated
      ...SPEAKER_LABEL_CONFIG[clip.asset_metadata.size].userName,
      maxWidth: '100%',
      lineHeight: 'none'
    }
  };

  const userTitle: SpeakerLabelTextInterface[] = title
    ? [
        {
          type: SpeakerLabelElementType.TEXT,
          content: title,
          style: {
            overflow: 'hidden',
            ...(['LANDSCAPE', 'PORTRAIT'].includes(clip.asset_metadata.size)
              ? {
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }
              : {
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }),
            ...SPEAKER_LABEL_CONFIG[clip.asset_metadata.size].userTitle,
            lineHeight: 'none',
            fontFamily: 'inherit',
            maxWidth: '100%',
            opacity: 0.7
          }
        }
      ]
    : [];

  if (clip.asset_metadata.size === 'LANDSCAPE') {
    return {
      type: SpeakerLabelElementType.CONTAINER,
      style: {
        ...commonContainerStyles,
        left: '50%',
        bottom: '0%',
        padding: '2%',
        maxWidth: '40%'
      },
      elements: [userName, ...userTitle],
      ...fontObject
    };
  }
  if (clip.asset_metadata.size === 'SQUARE') {
    return {
      type: SpeakerLabelElementType.CONTAINER,
      style: {
        ...commonContainerStyles,
        left: '85%',
        top: '0%',
        paddingInline: '2%',
        paddingBlock: '1%',
        transform: 'rotate(90deg) translateY(-100%)',
        transformOrigin: 'top left',
        maxWidth: '80%'
      },
      elements: [userName, ...userTitle],
      ...fontObject
    };
  }
  if (clip.asset_metadata.size === 'PORTRAIT') {
    return {
      type: SpeakerLabelElementType.CONTAINER,
      style: {
        ...commonContainerStyles,
        left: '2%',
        bottom: '2%',
        maxWidth: '80%'
      },
      elements: [userName, ...userTitle],
      ...fontObject
    };
  }
  return { type: SpeakerLabelElementType.CONTAINER, style: {}, elements: [] };
};
